// frontend/src/pages/dashboards/TileDialogs.jsx
// Phase 4 step 14a-3: the small dialogs the dashboard editor opens.
//   ChartPicker      choose a saved chart to add as a tile
//   TextTileDialog   write or change a text tile
//   TileTitleDialog  give a chart tile its own title (or go back to the chart's)
// All Radix dialogs: focus stays inside, Escape closes, focus returns.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'radix-ui';
import { Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { apiCall } from '../../utils/api';
import { MAX_TEXT, MAX_TILE_TITLE, legacyCount, pickableCharts, textProblem, typeLabel } from './dashboardState';

const CONTENT = 'fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-[22px] bg-[var(--ui-panel)] p-6 text-[var(--ui-text)] shadow-[0_24px_60px_rgba(0,0,0,0.6)] ring-1 ring-white/5 focus:outline-none';
const FIELD = 'w-full rounded-xl bg-[var(--ui-inset)] px-3 py-2 text-[13px] text-[var(--ui-text)] placeholder:text-[var(--ui-text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30';

function Shell({ open, onClose, children, width = 'max-w-md', initialFocus }) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          className={`${CONTENT} ${width}`}
          onOpenAutoFocus={initialFocus ? (e) => { e.preventDefault(); if (initialFocus.current) initialFocus.current.focus(); } : undefined}
        >
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ---- the chart picker ---- */

/**
 * open, onClose, onPick(chart), onDashboard: Set of chart ids already on the dashboard.
 * Lists the organization's charts (GET /charts/), builder charts only.
 */
export function ChartPicker({ open, onClose, onPick, onDashboard = new Set() }) {
  const [state, setState] = useState({ status: 'idle', charts: [], error: null });
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const ctrl = new AbortController();
    setQuery('');
    setState({ status: 'loading', charts: [], error: null });
    apiCall('/charts/', { signal: ctrl.signal })
      .then(async (res) => {
        if (!res.ok) throw new Error('failed');
        const body = await res.json();
        if (!ctrl.signal.aborted) setState({ status: 'ready', charts: body.charts || [], error: null });
      })
      .catch((error) => { if (!ctrl.signal.aborted) setState({ status: 'error', charts: [], error }); });
    return () => ctrl.abort();
  }, [open]);

  const shown = useMemo(() => pickableCharts(state.charts, query), [state.charts, query]);
  const older = legacyCount(state.charts);

  return (
    <Shell open={open} onClose={onClose} width="max-w-lg" initialFocus={searchRef}>
      <Dialog.Title className="mb-1 text-lg font-semibold tracking-[-0.01em]">Add a chart</Dialog.Title>
      <Dialog.Description className="mb-4 text-sm text-[var(--ui-text-muted)]">
        Pick one of your organization's saved charts. It keeps its own query and look; the dashboard only decides where it sits.
      </Dialog.Description>
      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--ui-text-muted)]" aria-hidden="true" />
        <input ref={searchRef} type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search charts"
          aria-label="Search charts" className={`${FIELD} h-9 pl-9`} />
      </div>
      <div className="max-h-[50vh] min-h-[120px] overflow-y-auto rounded-xl bg-[var(--ui-inset)] scrollbar-thin" role="listbox" aria-label="Charts">
        {state.status === 'loading' && (
          <p className="flex items-center justify-center gap-2 py-10 text-sm text-[var(--ui-text-muted)]" role="status">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading charts
          </p>
        )}
        {state.status === 'error' && <p className="py-10 text-center text-sm text-[var(--ui-text-muted)]">The charts couldn't be loaded. Close this and try again.</p>}
        {state.status === 'ready' && shown.length === 0 && (
          <p className="px-4 py-10 text-center text-sm text-[var(--ui-text-muted)]">
            {state.charts.length === 0 ? 'No saved charts yet. Make one in the chart builder first.'
              : query ? 'No chart matches that.' : 'Every saved chart here is from the previous builder. Update them on their chart pages first.'}
          </p>
        )}
        {state.status === 'ready' && shown.map((c) => {
          const on = onDashboard.has(c.id);
          return (
            <button key={c.id} type="button" role="option" aria-selected="false" onClick={() => onPick(c)}
              className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-[13px] hover:bg-[var(--ui-inset-hover)] focus:outline-none focus-visible:bg-[var(--ui-inset-hover)]">
              <span className="min-w-0">
                <span className="block truncate text-[var(--ui-text)]">{c.title}</span>
                <span className="block truncate text-xs text-[var(--ui-text-muted)]">
                  {typeLabel(c.chart_type)}{on ? ' · already on this dashboard' : ''}
                </span>
              </span>
              <span className="shrink-0 text-xs text-[var(--ui-text-muted)]">Add</span>
            </button>
          );
        })}
      </div>
      {state.status === 'ready' && older > 0 && shown.length > 0 && (
        <p className="mt-2 text-xs text-[var(--ui-text-muted)]">
          {older === 1 ? '1 chart from the previous builder is not shown.' : `${older} charts from the previous builder are not shown.`} Update them on their chart pages to use them here.
        </p>
      )}
      <div className="mt-4 flex justify-end">
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </div>
    </Shell>
  );
}

/* ---- a text tile ---- */

/** request: null, or { key: string | null, text: string } (key null = a new tile). onSave(text, key). */
export function TextTileDialog({ request, onClose, onSave }) {
  const [text, setText] = useState('');
  const ref = useRef(null);
  useEffect(() => { if (request) setText(request.text || ''); }, [request]);
  const problem = textProblem(text);
  const submit = (e) => {
    e.preventDefault();
    if (problem) return;
    onSave(text.trim(), request ? request.key : null);
  };
  return (
    <Shell open={Boolean(request)} onClose={onClose} initialFocus={ref}>
      <form onSubmit={submit}>
        <Dialog.Title className="mb-1 text-lg font-semibold tracking-[-0.01em]">{request && request.key ? 'Edit text' : 'Add text'}</Dialog.Title>
        <Dialog.Description className="mb-4 text-sm text-[var(--ui-text-muted)]">
          A heading or a note between the charts. Plain text; line breaks are kept.
        </Dialog.Description>
        <textarea ref={ref} value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={MAX_TEXT + 1}
          aria-label="Text" aria-invalid={Boolean(text) && Boolean(problem)} className={`${FIELD} resize-y leading-snug`} />
        <div className="mt-1 flex items-center justify-between text-xs text-[var(--ui-text-muted)]">
          <span role="alert">{text && problem ? problem : ''}</span>
          <span>{text.length.toLocaleString()} / {MAX_TEXT.toLocaleString()}</span>
        </div>
        <div className="mt-4 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={Boolean(problem)}>{request && request.key ? 'Save text' : 'Add text'}</Button>
        </div>
      </form>
    </Shell>
  );
}

/* ---- a chart tile's title ---- */

/** request: null, or { key, title: the override or '', chartTitle }. onSave(title, key): '' means use the chart's. */
export function TileTitleDialog({ request, onClose, onSave }) {
  const [title, setTitle] = useState('');
  const ref = useRef(null);
  useEffect(() => { if (request) setTitle(request.title || ''); }, [request]);
  const submit = (e) => { e.preventDefault(); onSave(title, request.key); };
  return (
    <Shell open={Boolean(request)} onClose={onClose} initialFocus={ref}>
      <form onSubmit={submit}>
        <Dialog.Title className="mb-1 text-lg font-semibold tracking-[-0.01em]">Tile title</Dialog.Title>
        <Dialog.Description className="mb-4 text-sm text-[var(--ui-text-muted)]">
          Shown above the chart on this dashboard only. Leave it empty to use the chart's own title.
        </Dialog.Description>
        <input ref={ref} type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={MAX_TILE_TITLE}
          placeholder={request ? request.chartTitle : ''} aria-label="Tile title" className={`${FIELD} h-9`} />
        <div className="mt-4 flex items-center justify-end gap-3">
          {request && request.title && (
            <Button type="button" variant="ghost" onClick={() => onSave('', request.key)}>Use the chart's title</Button>
          )}
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit">Save</Button>
        </div>
      </form>
    </Shell>
  );
}
